import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Divider, Alert, Autocomplete, Checkbox,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const money = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};
const BANK_SUBTYPES = ["Bank", "Credit Card"];

export default function BankReconciliation() {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [asOf, setAsOf] = useState(today());
  const [stmtBalance, setStmtBalance] = useState("");
  const [data, setData] = useState(null);
  const [cleared, setCleared] = useState({}); // lineId -> bool
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/accounting/chart")
      .then((r) => setBankAccounts((r.data || []).filter((a) => BANK_SUBTYPES.includes(a.SubType)).map((a) => a.AccountName)))
      .catch(() => setBankAccounts([]));
  }, []);

  const run = useCallback(() => {
    if (!account) { setError("Pick a bank account"); return; }
    setLoading(true); setError(null); setData(null); setCleared({});
    api.get("/accounting/bank-reconciliation", { params: { account, asOf } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build bank reconciliation"))
      .finally(() => setLoading(false));
  }, [account, asOf]);

  const toggle = (lineId) => setCleared((c) => ({ ...c, [lineId]: !c[lineId] }));

  const stats = useMemo(() => {
    if (!data) return null;
    const clearedSum = data.transactions.reduce((s, t) => s + (cleared[t.lineId] ? t.amount : 0), 0);
    const clearedBalance = Math.round(clearedSum * 100) / 100;
    const outstanding = Math.round((data.bookBalance - clearedBalance) * 100) / 100;
    const stmt = stmtBalance === "" ? null : Number(stmtBalance);
    const difference = stmt == null ? null : Math.round((stmt - clearedBalance) * 100) / 100;
    return { clearedBalance, outstanding, stmt, difference };
  }, [data, cleared, stmtBalance]);

  const reconciled = stats && stats.difference != null && Math.abs(stats.difference) < 0.005;

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Bank Reconciliation</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Match a bank/credit-card account to its statement. Tick each transaction that has cleared the bank, enter the
        statement's ending balance, and the worksheet tells you whether it reconciles. (Worksheet only — nothing is saved yet.)
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Autocomplete
            options={bankAccounts} value={account} onChange={(_e, v) => setAccount(v)}
            sx={{ minWidth: 260 }} size="small"
            renderInput={(params) => <TextField {...params} label="Bank account" />}
          />
          <TextField type="date" size="small" label="As of" InputLabelProps={{ shrink: true }} value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Load"}
          </Button>
        </Stack>
        {bankAccounts.length === 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
            No Bank / Credit Card accounts found in your Chart of Accounts yet.
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{data.account}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>As of {data.asOf}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: "center" }}>
            <TextField type="number" size="small" label="Statement ending balance" sx={{ minWidth: 220 }}
              value={stmtBalance} onChange={(e) => setStmtBalance(e.target.value)} />
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="body2">Book balance (per ledger): <b style={{ fontVariantNumeric: "tabular-nums" }}>{money(data.bookBalance)}</b></Typography>
              <Typography variant="body2">Cleared balance: <b style={{ fontVariantNumeric: "tabular-nums" }}>{money(stats.clearedBalance)}</b></Typography>
              <Typography variant="body2" color="text.secondary">Outstanding (not yet cleared): {money(stats.outstanding)}</Typography>
            </Box>
          </Stack>

          {stats.difference != null && (
            reconciled ? (
              <Alert severity="success" sx={{ mb: 2 }}>Reconciled — cleared balance matches the statement.</Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>Out by {money(Math.abs(stats.difference))}.</b> Statement ending ({money(stats.stmt)}) − cleared
                balance ({money(stats.clearedBalance)}). Keep ticking cleared items (or check the statement) until this is zero.
              </Alert>
            )
          )}

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ fontWeight: 700 }}>Cleared</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.transactions.length === 0 && (
                  <TableRow><TableCell colSpan={4} sx={{ color: "text.secondary" }}>No journal transactions on this account.</TableCell></TableRow>
                )}
                {data.transactions.map((t) => (
                  <TableRow key={t.lineId} hover selected={!!cleared[t.lineId]}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={!!cleared[t.lineId]} onChange={() => toggle(t.lineId)} />
                    </TableCell>
                    <TableCell sx={{ py: 0.3, whiteSpace: "nowrap" }}>{t.date}</TableCell>
                    <TableCell sx={{ py: 0.3 }}>{t.descr}</TableCell>
                    <TableCell align="right" sx={{ py: 0.3, fontVariantNumeric: "tabular-nums" }}>{money(t.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            Amount: positive = money into the account, negative = money out. Transactions come from Journal Entries posted
            to this account. Saving your cleared marks across sessions is a future enhancement.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
